import { useRef, useState } from 'react';
import type { FinancialProjection, FinancialSnapshot } from '@shared/types';
import { api } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import { useToast } from '../../lib/toast';
import { MONTH_NAMES, money } from '../../lib/format';
import { ProjectionChart } from '../../components/ProjectionChart';
import { Banner, EmptyState, Field, LoadingPage, Stat, Tag } from '../../components/ui';

type ParsedUpload = Awaited<ReturnType<typeof api.financeParse>>;

export function ManualSnapshots() {
  const state = useAsync(async () => api.financeCurrent(), []);
  const [showUpload, setShowUpload] = useState(false);

  if (state.loading && !state.data) return <LoadingPage label="Loading financials…" />;

  const snapshot = state.data?.snapshot ?? null;
  const projection = state.data?.projection ?? null;

  return (
    <div>
      <p className="page-lede">
        Quick manual snapshots: upload a CSV from your management company's portal, or enter the
        figures by hand, then see where spending lands by year end at the current burn rate.
        Approved monthly statements (in the Statements tab) are the preferred source.
      </p>

      <div className="row" style={{ marginBottom: 14 }}>
        <button type="button" className="btn" onClick={() => setShowUpload((open) => !open)}>
          {showUpload ? 'Close' : snapshot ? 'Upload a new snapshot' : 'Upload the first snapshot'}
        </button>
        {snapshot && (
          <span className="mut">
            Showing {snapshot.label} · posted by {snapshot.uploadedByName}
          </span>
        )}
      </div>

      {showUpload && (
        <SnapshotForm
          existing={snapshot}
          onSaved={(saved) => {
            state.setData(saved);
            setShowUpload(false);
          }}
        />
      )}

      {!snapshot || !projection ? (
        <EmptyState
          title="No financial snapshot yet."
          hint="Upload a CSV export from the management portal, or enter the figures by hand."
        />
      ) : (
        <SnapshotView snapshot={snapshot} projection={projection} />
      )}
    </div>
  );
}

/* --------------------------------------------------------------------- view */

function SnapshotView({
  snapshot,
  projection,
}: {
  snapshot: FinancialSnapshot;
  projection: FinancialProjection;
}) {
  const ytd = projection.cumulativeActual.filter((v): v is number => v !== null).at(-1) ?? 0;
  const maxVendor = Math.max(...projection.topVendors.map((v) => v.amount), 1);

  return (
    <>
      <div className="card">
        <div className="stat-grid">
          <Stat label="YTD expenses" value={money(ytd)} />
          <Stat label="Annual budget" value={money(projection.annualBudget)} />
          <Stat
            label="Projected year end"
            value={money(projection.projectedYearEnd)}
            tone={projection.overBudget ? 'bad' : 'ok'}
          />
          <Stat
            label={projection.overBudget ? 'Over budget by' : 'Under budget by'}
            value={money(Math.abs(projection.variance))}
            tone={projection.overBudget ? 'bad' : 'ok'}
          />
          {snapshot.operatingBalance !== null && (
            <Stat label="Operating balance" value={money(snapshot.operatingBalance)} />
          )}
          {snapshot.reserveBalance !== null && (
            <Stat label="Reserves" value={money(snapshot.reserveBalance)} />
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Cumulative expenses vs. budget</h3>
          <Tag tone={projection.overBudget ? 'bad' : 'ok'}>
            {projection.overBudget ? 'Trending over' : 'Trending under'}
          </Tag>
        </div>
        <p className="mut">
          Solid line is actual spend through {MONTH_NAMES[snapshot.throughMonth - 1]}. The dashed
          line carries the average monthly burn of {money(projection.monthlyBurnRate)} through
          December.
        </p>
        <ProjectionChart
          actual={projection.cumulativeActual}
          projected={projection.cumulativeProjected}
          annualBudget={projection.annualBudget}
        />
        {projection.overBudget && (
          <Banner kind="warn">
            At the current pace the association finishes the year{' '}
            {money(Math.abs(projection.variance))} over budget. Closing the gap means cutting about{' '}
            {money(Math.abs(projection.variance) / Math.max(1, 12 - snapshot.throughMonth))} per
            remaining month.
          </Banner>
        )}
      </div>

      <div className="grid two">
        <div className="card tw">
          <div className="card-head">
            <h3>Delinquent accounts</h3>
            <Tag tone={projection.delinquentTotal > 0 ? 'warn' : 'ok'}>
              {money(projection.delinquentTotal)}
            </Tag>
          </div>
          {snapshot.delinquents.length === 0 ? (
            <p className="mut">No delinquent balances. Nice.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Unit</th>
                  <th className="num">Balance</th>
                  <th className="num">Days</th>
                </tr>
              </thead>
              <tbody>
                {snapshot.delinquents.map((account) => (
                  <tr key={account.id}>
                    <td>
                      {account.unit}
                      {account.inCollections && <Tag tone="bad">Collections</Tag>}
                    </td>
                    <td className="num">{money(account.amount, { cents: true })}</td>
                    <td className={`num ${account.daysPastDue >= 90 ? 'bad' : ''}`}>
                      {account.daysPastDue}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card">
          <h3>Expenses by vendor</h3>
          {projection.topVendors.length === 0 ? (
            <p className="mut">No vendor detail in this snapshot.</p>
          ) : (
            projection.topVendors.map((vendor) => (
              <div key={vendor.id} style={{ marginTop: 10 }}>
                <div className="row between">
                  <span>
                    {vendor.vendor}
                    {vendor.category && <span className="mut"> · {vendor.category}</span>}
                  </span>
                  <span className="mut nowrap">{money(vendor.amount)}</span>
                </div>
                <div className="bar-track">
                  <div className="bar" style={{ width: `${(vendor.amount / maxVendor) * 100}%` }} />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {snapshot.monthly.length > 0 && (
        <div className="card tw">
          <h3>Monthly detail</h3>
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th className="num">Income</th>
                <th className="num">Expenses</th>
                <th className="num">Net</th>
              </tr>
            </thead>
            <tbody>
              {snapshot.monthly.map((row) => (
                <tr key={row.month}>
                  <td>{MONTH_NAMES[row.month - 1]}</td>
                  <td className="num">{money(row.income)}</td>
                  <td className="num">{money(row.expenses)}</td>
                  <td className={`num ${row.income - row.expenses < 0 ? 'bad' : 'ok'}`}>
                    {money(row.income - row.expenses)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(snapshot.notes || snapshot.sourceUrl) && (
        <div className="card">
          <h3>Source</h3>
          {snapshot.notes && <p className="pre-wrap">{snapshot.notes}</p>}
          {snapshot.sourceUrl && (
            <a className="btn s tiny" href={snapshot.sourceUrl} target="_blank" rel="noreferrer">
              {snapshot.sourceFilename ?? 'Original file'}
            </a>
          )}
        </div>
      )}
    </>
  );
}

/* --------------------------------------------------------------------- form */

interface DelinquentRow {
  unit: string;
  ownerName: string | null;
  amount: number;
  daysPastDue: number;
  inCollections: boolean;
}
interface VendorRow {
  vendor: string;
  category: string | null;
  amount: number;
}
interface MonthlyRow {
  month: number;
  income: number;
  expenses: number;
}

function SnapshotForm({
  existing,
  onSaved,
}: {
  existing: FinancialSnapshot | null;
  onSaved: (data: { snapshot: FinancialSnapshot; projection: FinancialProjection }) => void;
}) {
  const { notify, fail } = useToast();
  const now = new Date();
  const fileInput = useRef<HTMLInputElement>(null);

  const [meta, setMeta] = useState({
    year: existing?.year ?? now.getFullYear(),
    throughMonth: existing?.throughMonth ?? Math.max(1, now.getMonth()),
    annualBudget: existing?.annualBudget?.toString() ?? '',
    reserveBalance: existing?.reserveBalance?.toString() ?? '',
    operatingBalance: existing?.operatingBalance?.toString() ?? '',
    notes: '',
  });
  const [delinquents, setDelinquents] = useState<DelinquentRow[]>(existing?.delinquents ?? []);
  const [vendors, setVendors] = useState<VendorRow[]>(existing?.vendors ?? []);
  const [monthly, setMonthly] = useState<MonthlyRow[]>(existing?.monthly ?? []);
  const [parsed, setParsed] = useState<ParsedUpload | null>(null);
  const [sourceAttachmentId, setSourceAttachmentId] = useState<string | null>(null);
  const [sourceFilename, setSourceFilename] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleFile(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    try {
      // Keep the original document on the record regardless of whether we can parse it.
      const { attachments } = await api.upload([file]);
      setSourceAttachmentId(attachments[0].id);
      setSourceFilename(file.name);

      if (/\.csv$/i.test(file.name)) {
        const result = await api.financeParse(file);
        setParsed(result);
        if (result.parsed.delinquents.length > 0) setDelinquents(result.parsed.delinquents);
        if (result.parsed.vendors.length > 0) setVendors(result.parsed.vendors);
        if (result.parsed.monthly.length > 0) {
          setMonthly(result.parsed.monthly);
          const lastMonth = Math.max(...result.parsed.monthly.map((m) => m.month));
          setMeta((current) => ({ ...current, throughMonth: lastMonth }));
        }
        notify('File parsed. Check the figures below before saving.', 'success');
      } else {
        notify('File attached. Enter the figures below — only CSV can be parsed automatically.');
      }
    } catch (error) {
      fail(error, 'That file could not be read.');
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function save() {
    if (!meta.annualBudget) {
      notify('Enter the annual budget so the projection has something to compare against.', 'error');
      return;
    }
    setBusy(true);
    try {
      const result = await api.financeSave({
        year: meta.year,
        throughMonth: meta.throughMonth,
        label: `${meta.year} YTD through ${MONTH_NAMES[meta.throughMonth - 1]}`,
        annualBudget: Number(meta.annualBudget),
        reserveBalance: meta.reserveBalance ? Number(meta.reserveBalance) : null,
        operatingBalance: meta.operatingBalance ? Number(meta.operatingBalance) : null,
        notes: meta.notes || undefined,
        sourceFilename: sourceFilename ?? undefined,
        sourceAttachmentId,
        delinquents,
        vendors,
        monthly,
      });
      onSaved(result);
      notify('Snapshot saved.', 'success');
    } catch (error) {
      fail(error, 'The snapshot was not saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h3>Upload a management company snapshot</h3>
      <p className="mut">
        CSV exports are parsed automatically when they use <code>#&nbsp;delinquents</code>,{' '}
        <code>#&nbsp;vendors</code> and <code>#&nbsp;monthly</code> section headings. PDFs are stored
        for the record; enter their figures by hand.
      </p>

      <label className="picker" style={{ marginTop: 8 }}>
        <span aria-hidden="true">⬆</span> Choose file
        <input
          ref={fileInput}
          type="file"
          accept=".csv,.pdf,.xlsx,.xls"
          onChange={(event) => void handleFile(event.target.files?.[0])}
        />
      </label>
      {sourceFilename && <span className="mut"> {sourceFilename}</span>}

      {parsed && parsed.parsed.warnings.length > 0 && (
        <Banner kind="warn">
          {parsed.parsed.warnings.map((warning, index) => (
            <div key={index}>{warning}</div>
          ))}
        </Banner>
      )}

      <hr className="divider" />

      <div className="grid two">
        <Field label="Year">
          <input
            type="number"
            value={meta.year}
            onChange={(event) => setMeta({ ...meta, year: Number(event.target.value) })}
          />
        </Field>
        <Field label="Actuals through">
          <select
            value={meta.throughMonth}
            onChange={(event) => setMeta({ ...meta, throughMonth: Number(event.target.value) })}
          >
            {MONTH_NAMES.map((name, index) => (
              <option key={name} value={index + 1}>
                {name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Annual budget">
          <input
            type="number"
            min={0}
            inputMode="decimal"
            value={meta.annualBudget}
            onChange={(event) => setMeta({ ...meta, annualBudget: event.target.value })}
            placeholder="148000"
            required
          />
        </Field>
        <Field label="Operating balance" hint="Optional.">
          <input
            type="number"
            min={0}
            inputMode="decimal"
            value={meta.operatingBalance}
            onChange={(event) => setMeta({ ...meta, operatingBalance: event.target.value })}
          />
        </Field>
        <Field label="Reserve balance" hint="Optional.">
          <input
            type="number"
            min={0}
            inputMode="decimal"
            value={meta.reserveBalance}
            onChange={(event) => setMeta({ ...meta, reserveBalance: event.target.value })}
          />
        </Field>
      </div>

      <Field label="Notes" hint="Anything the board should know about these numbers.">
        <textarea
          rows={2}
          value={meta.notes}
          onChange={(event) => setMeta({ ...meta, notes: event.target.value })}
        />
      </Field>

      <EditableRows
        title="Monthly actuals"
        empty="Add each month's income and expenses so the projection has a burn rate."
        rows={monthly}
        onAdd={() =>
          setMonthly([
            ...monthly,
            { month: Math.min(12, monthly.length + 1), income: 0, expenses: 0 },
          ])
        }
        onRemove={(index) => setMonthly(monthly.filter((_, i) => i !== index))}
        render={(row, index) => (
          <>
            <select
              value={row.month}
              onChange={(event) =>
                setMonthly(
                  monthly.map((r, i) => (i === index ? { ...r, month: Number(event.target.value) } : r)),
                )
              }
              style={{ margin: 0 }}
            >
              {MONTH_NAMES.map((name, i) => (
                <option key={name} value={i + 1}>
                  {name}
                </option>
              ))}
            </select>
            <input
              type="number"
              value={row.income}
              aria-label="Income"
              onChange={(event) =>
                setMonthly(
                  monthly.map((r, i) => (i === index ? { ...r, income: Number(event.target.value) } : r)),
                )
              }
              style={{ margin: 0 }}
            />
            <input
              type="number"
              value={row.expenses}
              aria-label="Expenses"
              onChange={(event) =>
                setMonthly(
                  monthly.map((r, i) =>
                    i === index ? { ...r, expenses: Number(event.target.value) } : r,
                  ),
                )
              }
              style={{ margin: 0 }}
            />
          </>
        )}
      />

      <EditableRows
        title="Expenses by vendor"
        empty="Optional, but it makes the newsletter and budget conversations much easier."
        rows={vendors}
        onAdd={() => setVendors([...vendors, { vendor: '', category: null, amount: 0 }])}
        onRemove={(index) => setVendors(vendors.filter((_, i) => i !== index))}
        render={(row, index) => (
          <>
            <input
              value={row.vendor}
              placeholder="Vendor"
              onChange={(event) =>
                setVendors(vendors.map((r, i) => (i === index ? { ...r, vendor: event.target.value } : r)))
              }
              style={{ margin: 0 }}
            />
            <input
              value={row.category ?? ''}
              placeholder="Category"
              onChange={(event) =>
                setVendors(
                  vendors.map((r, i) => (i === index ? { ...r, category: event.target.value || null } : r)),
                )
              }
              style={{ margin: 0 }}
            />
            <input
              type="number"
              value={row.amount}
              aria-label="Amount"
              onChange={(event) =>
                setVendors(
                  vendors.map((r, i) => (i === index ? { ...r, amount: Number(event.target.value) } : r)),
                )
              }
              style={{ margin: 0 }}
            />
          </>
        )}
      />

      <EditableRows
        title="Delinquent accounts"
        empty="Units carrying a past-due balance."
        rows={delinquents}
        onAdd={() =>
          setDelinquents([
            ...delinquents,
            { unit: '', ownerName: null, amount: 0, daysPastDue: 0, inCollections: false },
          ])
        }
        onRemove={(index) => setDelinquents(delinquents.filter((_, i) => i !== index))}
        render={(row, index) => (
          <>
            <input
              value={row.unit}
              placeholder="Unit"
              onChange={(event) =>
                setDelinquents(
                  delinquents.map((r, i) => (i === index ? { ...r, unit: event.target.value } : r)),
                )
              }
              style={{ margin: 0 }}
            />
            <input
              type="number"
              value={row.amount}
              aria-label="Balance"
              onChange={(event) =>
                setDelinquents(
                  delinquents.map((r, i) => (i === index ? { ...r, amount: Number(event.target.value) } : r)),
                )
              }
              style={{ margin: 0 }}
            />
            <input
              type="number"
              value={row.daysPastDue}
              aria-label="Days past due"
              onChange={(event) =>
                setDelinquents(
                  delinquents.map((r, i) =>
                    i === index ? { ...r, daysPastDue: Number(event.target.value) } : r,
                  ),
                )
              }
              style={{ margin: 0 }}
            />
          </>
        )}
      />

      <div className="btn-row">
        <button type="button" className="btn" disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save snapshot'}
        </button>
      </div>
    </div>
  );
}

function EditableRows<T>({
  title,
  empty,
  rows,
  onAdd,
  onRemove,
  render,
}: {
  title: string;
  empty: string;
  rows: T[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  render: (row: T, index: number) => React.ReactNode;
}) {
  return (
    <fieldset style={{ marginTop: 14 }}>
      <legend>{title}</legend>
      {rows.length === 0 && <p className="mut">{empty}</p>}
      <div className="stack" style={{ gap: 8 }}>
        {rows.map((row, index) => (
          <div
            key={index}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr)) auto',
              gap: 8,
              alignItems: 'center',
            }}
          >
            {render(row, index)}
            <button
              type="button"
              className="btn ghost tiny"
              onClick={() => onRemove(index)}
              aria-label="Remove row"
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <div className="btn-row">
        <button type="button" className="btn s tiny" onClick={onAdd}>
          Add row
        </button>
      </div>
    </fieldset>
  );
}
